import { SupportAlert } from '@/components/support-alert';
import { reopenTicket } from '@/ticket/api/reopen-ticket';
import { Ticket } from '@/ticket/api/types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  isOpen: boolean;
  ticket: Ticket;
  close: () => void;
}

export function TicketReopenDialog(props: Props) {
  const [reopenReason, setReopenReason] = useState<string>('');
  const [showError, setShowError] = useState<boolean | undefined>(undefined);

  const queryClient = useQueryClient();

  const reopenTicketMutation = useMutation({
    mutationFn: (reason: string) => {
      const refinedReason = reason.trim();
      return reopenTicket(props.ticket.key, {
        reopenReason: refinedReason === '' ? undefined : refinedReason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticket', props.ticket.key],
      });
      setReopenReason('');
      setShowError(false);
      props.close();
    },
    onError: () => {
      setShowError(true);
    },
  });

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={props.close}>
      <DialogTitle>Reopen ticket</DialogTitle>
      <DialogContent>
        {showError && <SupportAlert message={'Something went wrong.'} />}
        <TextField
          autoFocus
          margin="dense"
          label="Reason (optional)"
          placeholder="(Optional) Provide the reason for reopening this ticket"
          fullWidth
          multiline
          rows={4}
          value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setReopenReason('');
            setShowError(false);
            props.close();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => reopenTicketMutation.mutate(reopenReason)}
          color="primary"
          variant="contained"
        >
          Reopen ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
}
